import {
    OrderSide,
    PaymentActionTypes,
    SelectOption,
    doesWorkflowRejectCodeExist
} from '@tassat/common-definitions';
import { TransactForm } from '@tassat/forms';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { SecurityCurrencyAssetPair, WalletRecordExtended } from '@tassat/fred-extended-definitions';
import { convertToNumber } from '@tassat/utility';
import React from 'react';

import TransactConfirmation from 'Containers/client/forms/transactConfirmation';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAllSecurityCurrencyPairs, getEntitledAssetsByTransactionType } from 'Selectors/assets';
import { getClientLiquidityFacilityStatus, getPrices, getSelectedClientAccount } from 'Selectors/common';
import { getClientFirm } from 'Selectors/firms';
import { getNetAssetValuesByAssetId } from 'Selectors/netAssetValues';
import { ActionOverlay } from 'SharedComponents/layouts/overlays';
import WalletCompactDetails from 'SharedComponents/wallets/WalletCompactDetails';

import type { ITransactPaymentProps } from '.';

export function TransactPayment(props: ITransactPaymentProps) {
    const {
        title,
        transactionValues,
        setTransactionValues,
        handleTransact,
        goBack,
        accountPositionBalance,
        allAssets
    } = props;

    const currentWallet = useAppSelector(getSelectedClientAccount) as WalletRecordExtended;
    const isLiquidityFacilityAvailable = useAppSelector(getClientLiquidityFacilityStatus);
    const currentFirm = useAppSelector(getClientFirm);
    const entitledAssetsByType = useAppSelector(
        (state) => getEntitledAssetsByTransactionType(state, currentFirm.FirmId)
    );
    const navPriceList = useAppSelector(getNetAssetValuesByAssetId);
    const realTimePriceList = useAppSelector(getPrices);
    const allSecurityCurrencyPairs = useAppSelector(getAllSecurityCurrencyPairs);
    const [ selectedAssetPair, setSelectedAssetPair ] = React.useState<SecurityCurrencyAssetPair>(allSecurityCurrencyPairs[0] ?? {});
    const [ confirm, setConfirm ] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (allSecurityCurrencyPairs.length > 0 && !selectedAssetPair.security) {
            setSelectedAssetPair(allSecurityCurrencyPairs[0]);
        }
    }, [allSecurityCurrencyPairs]);

    const navPrice = React.useMemo(() => {
        if (!navPriceList || !selectedAssetPair.security?.AssetId) return;

        return navPriceList[selectedAssetPair.security.AssetId];
    }, [ navPriceList, selectedAssetPair.security?.AssetId ]);

    const realTimePrice = React.useMemo(() => {
        if (!realTimePriceList) return;
        const matched = Object.keys(realTimePriceList)
            .filter(key => realTimePriceList[key]?.AssetId === selectedAssetPair.security?.AssetId)
            .map(key => realTimePriceList[key]);

        return matched.length ? matched[0] : undefined;
    }, [ realTimePriceList, selectedAssetPair.security?.AssetId ]);

    const walletDetailHeader = React.useMemo(() => {
        return (
            <WalletCompactDetails
                accountName={currentWallet.AccountName}
                accountNo={currentWallet.linkedBrokerDealerAccount ?? ''}
            />
        );
    }, [
        currentWallet.AccountName,
        currentWallet.Account
    ]);

    React.useEffect(() => {
        setTransactionValues({
            ...transactionValues,
            Wallet: currentWallet
        });
    }, [currentWallet]);

    const confirmTransact = (values) => {
        setTransactionValues({
            ...values,
            Wallet: currentWallet
        });
        setConfirm(true);
    };

    const assetTypes: SelectOption[] = allSecurityCurrencyPairs.map(pair => ({
        label: `${pair.security?.Ticker} - ${pair.security?.Description}`,
        value: pair.security?.AssetId ?? '',
        refData: pair.security
    }));

    const handleAssetChange = (assetId: string) => {
        const matched = allSecurityCurrencyPairs.find(pair => pair.security?.AssetId === assetId);
        if (matched) setSelectedAssetPair(matched);
    };

    const feeAmount = async (transactionValues: ITransactFormValues) => {
        try {
            const response = await Worker.getInstance()
                .getFeeByOrderDetails({
                    FirmId: currentWallet.FirmId,
                    AccountId: currentWallet.AccountId,
                    Ticker: (OrderSide.REDEEM === transactionValues.Type || OrderSide.SELL === transactionValues.Type) ?
                        selectedAssetPair.security?.Ticker : selectedAssetPair.currency?.Ticker,
                    OrderSide: transactionValues.Type,
                    Amount: convertToNumber(transactionValues.Amount ?? ''),
                    Price: (OrderSide.BUY === transactionValues.Type || OrderSide.SELL === transactionValues.Type) ?
                        realTimePrice?.Price : navPrice?.Price
                });
            if (response.data && (response.data.Status === 49 || doesWorkflowRejectCodeExist(response.data.Status))) {
                return 'Could not receive fee.';
            } else {
                return response.data;
            }
        } catch (error) {
            return 'Could not receive fee, please try again';
        }
    };

    const accountPositionCcyBalanceRaw = accountPositionBalance[0].ccy[selectedAssetPair.currency?.AssetId ?? ''];
    const accountPositionCcyBalance = accountPositionCcyBalanceRaw ? {
        availableBalance: accountPositionCcyBalanceRaw.availableBalance.toNumber(),
        currentBalance: accountPositionCcyBalanceRaw.currentBalance.toNumber()
    } : undefined;

    const accountPositionSecurityBalanceRaw = accountPositionBalance[0].security[selectedAssetPair.security?.AssetId ?? ''];
    const accountPositionSecurityBalance = accountPositionSecurityBalanceRaw ? {
        availableBalance: accountPositionSecurityBalanceRaw.availableBalance.toNumber(),
        currentBalance: accountPositionSecurityBalanceRaw.currentBalance.toNumber()
    } : undefined;

    const getAccount = async (account, currency) => Worker.getInstance()
        .getAccount(account, currency);
    const getTransactionsSync = async (params) => Worker.getInstance()
        .getTransactionsSync(params);

    return (
        <ActionOverlay
            admin={false}
            goBack={goBack}
            headerDetailsComponent={walletDetailHeader}
            title={title}
            type={PaymentActionTypes['transact'].type}
        >
            {confirm ?
                <TransactConfirmation
                    allAssets={allAssets}
                    assetPair={selectedAssetPair}
                    cancelConfirmation={() => {
                        if (transactionValues && transactionValues.Amount && typeof transactionValues.Amount === 'number') {
                            setTransactionValues({
                                ...transactionValues,
                                Amount: `$${transactionValues.Amount}`
                            });
                        }
                        setConfirm(false);
                    }}
                    handleTransact={handleTransact}
                    navPrice={navPrice}
                    transactValues={transactionValues}
                />
                : (
                    <TransactForm
                        formProps={
                            {
                                currentWallet,
                                transactType: 'subscription',
                                assetTypes: assetTypes,
                                selectedWallet: currentWallet,
                                savedTransactionValues: transactionValues,
                                handleTransact: confirmTransact,
                                getAccount,
                                getTransactionsSync,
                                feeAmount,
                                navPrice: navPrice,
                                realTimePrice: realTimePrice,
                                accountPositionCcyBalance,
                                accountPositionSecurityBalance,
                                orderSide: '',
                                assetPair: selectedAssetPair,
                                onAssetChange: handleAssetChange,
                                currentFirm,
                                isLiquidityFacilityAvailable,
                                entitledAssetsByType
                            }
                        }
                    />
                )}

        </ActionOverlay>
    );
}